import { useIsomorphicLayoutEffect } from '@cian/react-utils';

import { type ReactNode, type RefObject, useState } from 'react';
import { createPortal } from 'react-dom';

import { type IEventEmitter } from 'shared/services/eventEmitter';
import { type TAdfoxBannerOnLoadData } from 'shared/types/adfox';

import { Ticker } from 'shared/components/Ticker';

import { useBannerOverlay } from './useBannerOverlay';

export type HTMLBannerInsertsParams = {
  rootRef: RefObject<HTMLDivElement | null>;
  adfoxBannerOnLoadData: TAdfoxBannerOnLoadData | null;
  eventEmitter: IEventEmitter;
};

/**
 * Добавляет служебные элементы (например, маркировку рекламы) в HTML-баннеры.
 *
 * Вставка идёт тем же механизмом, что у шаблона и скелетона: overlay-контейнер
 * внутри рутового div + портал. Это не перезаписывает разметку, вставленную
 * библиотекой adfox, и сохраняет React-контекст (в отличие от отдельного createRoot).
 */
export function useHTMLBannerInserts({
  rootRef,
  adfoxBannerOnLoadData,
  eventEmitter,
}: HTMLBannerInsertsParams): ReactNode {
  // Кладём маркировку в стейт только по событию `render`: к этому моменту adfox уже
  // вставил креатив интерскроллера в контейнер. Если вставить overlay раньше (на
  // `load`), рендер adfox его затрёт.
  const [adLabelText, setAdLabelText] = useState<string | null>(null);

  useIsomorphicLayoutEffect(() => {
    const adLabelTextValue =
      adfoxBannerOnLoadData?.bundleName === 'banner.interscroller'
        ? adfoxBannerOnLoadData.bundleParams.adLabelText
        : null;

    if (adLabelTextValue) {
      const unsubscribe = eventEmitter.subscribe('render', () => {
        setAdLabelText(adLabelTextValue);

        unsubscribe();
      });

      return (): void => unsubscribe();
    }

    return;
  }, [eventEmitter, adfoxBannerOnLoadData]);

  const overlayElement = useBannerOverlay({ enabled: !!adLabelText, placement: 'bottom', rootRef, zIndex: 1 });

  return overlayElement && adLabelText
    ? createPortal(<Ticker text={adLabelText} theme="dark-background" />, overlayElement)
    : null;
}
